import React, { useState } from "react";
import { Redirect } from "react-router-dom";
import LayoutWithSidebar from "../components/LayoutWithSidebar";
import MainSideMenu from "../components/MainSideMenu";
import MainHeaderContainer from "../containers/MainHeaderContainer";
import PermitRequestApprovalStatsContainer from "../containers/PermitRequestApprovalStatsContainer";
import GroupedPermitsBreakdownContainer from "../containers/GroupedPermitsBreakdownContainer";
import PermitInProgressAvgDurationContainer from "../containers/PermitInProgressAvgDurationContainer";
import PermitExpiredAvgDurationContainer from "../containers/PermitExpiredAvgDurationContainer";
import RegisterPermitIssueTrendContainer from "../containers/RegisterPermitIssueTrendContainer";
import DrillThroughPermitsModal from "../components/DrillThroughPermitsModal";
import { DynamicScopeSelector } from "../components/DynamicScopeSelector";
import { useAppContext } from "../hooks/useAppContext";
import { useOrganizationPolicies } from "../hooks/useOrganizationPolicies";
import { PermitMetricScopeInput } from "../operations/queries/getPermitsCountByStatesMetric";
import { Segment, Header, Grid } from "semantic-ui-react";

const OrgMetricsDashboardPage = () => {
  const { currentOrganization } = useAppContext();
  const { organizationPolicies, authorizedToViewOrganizationDashboard } = useOrganizationPolicies();

  const [scope, setScope] = useState<PermitMetricScopeInput>({
    organizationId: currentOrganization?.id || "",
    projectIds: null,
    registerIds: null,
    teamMemberIds: null,
    dateFrom: null,
    dateTo: null,
  });

  // Drill-through modal state, driven by the grouped breakdown's "View permits"
  // action. The container builds the narrowed scope and title for the selection.
  const [drillThroughOpen, setDrillThroughOpen] = useState(false);
  const [drillThroughScope, setDrillThroughScope] = useState<PermitMetricScopeInput>(scope);
  const [drillThroughTitle, setDrillThroughTitle] = useState("Permits");

  const handleViewPermits = (narrowedScope: PermitMetricScopeInput, title: string) => {
    setDrillThroughScope(narrowedScope);
    setDrillThroughTitle(title);
    setDrillThroughOpen(true);
  };

  // Ensure scope's organization keeps updated when AppContext loads
  React.useEffect(() => {
    if (currentOrganization && !scope.organizationId) {
      setScope((prev) => ({ ...prev, organizationId: currentOrganization.id }));
    }
  }, [currentOrganization, scope.organizationId]);

  const headerContent = () => {
    return <MainHeaderContainer></MainHeaderContainer>;
  };

  const sidebarContent = () => {
    return <MainSideMenu></MainSideMenu>;
  };

  const contentPanelContent = () => {
    if (!currentOrganization) return null;

    return (
      <>
        <Segment basic>
          <Header as="h2">
            <Header.Content>
              Organization Metrics Dashboard
              <Header.Subheader>
                Aggregated permit analytics for <strong>{currentOrganization.name}</strong>
              </Header.Subheader>
            </Header.Content>
          </Header>
        </Segment>
        <DynamicScopeSelector value={scope} onChange={setScope} />

        <Grid stackable columns={1}>
          <Grid.Column>
            <GroupedPermitsBreakdownContainer scope={scope} onViewPermits={handleViewPermits} />
          </Grid.Column>
        </Grid>
        <Grid stackable columns={1}>
          <Grid.Column>
            <RegisterPermitIssueTrendContainer scope={scope} />
          </Grid.Column>
        </Grid>
        <Grid stackable columns={3}>
          <Grid.Column>
            <Segment style={{ height: "100%" }}>
              <PermitRequestApprovalStatsContainer scope={scope} />
            </Segment>
          </Grid.Column>
          <Grid.Column>
            <Segment style={{ height: "100%" }}>
              <PermitInProgressAvgDurationContainer scope={scope} />
            </Segment>
          </Grid.Column>
          <Grid.Column>
            <Segment style={{ height: "100%" }}>
              <PermitExpiredAvgDurationContainer scope={scope} />
            </Segment>
          </Grid.Column>
        </Grid>

        <DrillThroughPermitsModal
          open={drillThroughOpen}
          title={drillThroughTitle}
          scope={drillThroughScope}
          onClose={() => setDrillThroughOpen(false)}
        />
      </>
    );
  };

  if (currentOrganization && organizationPolicies && !authorizedToViewOrganizationDashboard) {
    return <Redirect to={`/${currentOrganization.name}/home`} />;
  }

  return (
    <LayoutWithSidebar
      headerContent={headerContent()}
      sidebarContent={sidebarContent()}
      mainPanelContent={contentPanelContent()}
    ></LayoutWithSidebar>
  );
};

export default OrgMetricsDashboardPage;
